/**
 * useIssueContext — collects browser, console errors, and recent API call context
 * for automatic inclusion in bug reports.
 */
import { useRef, useEffect, useCallback } from 'react';

const MAX_CONSOLE = 10;
const MAX_API_CALLS = 5;

const MODULE_MAP = {
  // Route path segment → display name
  ekonomi: 'Ekonomi',
  finance: 'Ekonomi',
  tidplan: 'Tidplan',
  schedule: 'Tidplan',
  avvikelser: 'Avvikelsehantering',
  deviations: 'Avvikelsehantering',
  dagbok: 'Dagbok',
  diary: 'Dagbok',
  inkop: 'Inköpsmodul',
  purchasing: 'Inköpsmodul',
  arenden: 'Ärendehantering',
  issues: 'Ärendehantering',
  anbud: 'Anbudsmodul',
  tenders: 'Anbudsmodul',
  bilder: 'Bilder',
  images: 'Bilder',
  ritningar: 'Ritningar',
  drawings: 'Ritningar',
  skyddsronder: 'Skyddsronder',
  safety: 'Skyddsronder',
  kanai: 'KanAI',
  ai: 'KanAI',
  betalplan: 'Ekonomi – Betalplan',
  bak: 'Ekonomi – BÅK/Mängdavstämning',
  mangdavstamning: 'Ekonomi – BÅK/Mängdavstämning',
  leverantorer: 'Leverantörsmodul',
  suppliers: 'Leverantörsmodul',
  avtal: 'Avtalsmodul',
  contracts: 'Avtalsmodul',
  kalkyl: 'Kalkylmodul',
  kancalc: 'Kalkylmodul',
  fleet: 'Maskinpark',
  feedback: 'Feedback',
  projects: 'Projekt',
  meetings: 'Mötesmodul',
  training: 'Utbildning',
  revisions: 'Revisionsmodul',
  klimat: 'Klimatmodul',
  orgplan: 'Projektorganisation',
  equippass: 'EquipPass',
  bim: 'BIM',
  home: 'Hem/Översikt',
  dashboard: 'Dashboard',
  admin: 'Administration',
};

function parseModuleFromPath(path) {
  const segments = (path || '').split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (MODULE_MAP[segments[i]]) return MODULE_MAP[segments[i]];
  }
  return 'Övrigt';
}

export function useIssueContext() {
  const consoleLog = useRef([]);
  const apiLog = useRef([]);

  useEffect(() => {
    const origError = console.error;
    const origWarn = console.warn;

    console.error = (...args) => {
      consoleLog.current = [
        ...consoleLog.current.slice(-(MAX_CONSOLE - 1)),
        { level: 'error', message: String(args[0] || ''), timestamp: new Date().toISOString() },
      ];
      origError.apply(console, args);
    };
    console.warn = (...args) => {
      consoleLog.current = [
        ...consoleLog.current.slice(-(MAX_CONSOLE - 1)),
        { level: 'warn', message: String(args[0] || ''), timestamp: new Date().toISOString() },
      ];
      origWarn.apply(console, args);
    };

    return () => {
      console.error = origError;
      console.warn = origWarn;
    };
  }, []);

  useEffect(() => {
    const origFetch = window.fetch;

    window.fetch = async (input, init) => {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      const method = init?.method ?? 'GET';
      const start = performance.now();

      try {
        const response = await origFetch(input, init);
        apiLog.current = [
          ...apiLog.current.slice(-(MAX_API_CALLS - 1)),
          {
            method,
            url: url.length > 120 ? url.slice(0, 120) + '…' : url,
            status: response.status,
            duration_ms: Math.round(performance.now() - start),
            timestamp: new Date().toISOString(),
          },
        ];
        return response;
      } catch (err) {
        apiLog.current = [
          ...apiLog.current.slice(-(MAX_API_CALLS - 1)),
          {
            method,
            url: url.length > 120 ? url.slice(0, 120) + '…' : url,
            status: 0,
            duration_ms: Math.round(performance.now() - start),
            timestamp: new Date().toISOString(),
          },
        ];
        throw err;
      }
    };

    return () => { window.fetch = origFetch; };
  }, []);

  const getContext = useCallback(() => {
    const ua = navigator.userAgent;
    const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
    const osMatch = ua.match(/(Windows NT \d+\.\d+|Mac OS X [\d_]+|Linux)/);

    return {
      url: window.location.pathname,
      browser: browserMatch ? `${browserMatch[1]} ${browserMatch[2]}` : ua.slice(0, 50),
      os: osMatch ? osMatch[1].replace(/_/g, '.') : 'Unknown',
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      session_id: (sessionStorage.getItem('session_id') || localStorage.getItem('kanplan-session-id') || 'unknown').slice(0, 12),
      tenant_id: localStorage.getItem('kanplan-active-tenant-slug') || 'unknown',
      user_role: localStorage.getItem('kanplan-user-role') || 'unknown',
      module: parseModuleFromPath(window.location.pathname),
      console_errors: [...consoleLog.current],
      recent_api_calls: [...apiLog.current],
    };
  }, []);

  return getContext;
}
